import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { forwardRef, useId, type ReactNode } from "react";
import styled from "@emotion/styled";
import { twRing } from "@/lib/emotionTw";

export type RadioOption = {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
};

export type RadioProps = RadioGroupPrimitive.RadioGroupProps & {
  options: RadioOption[];
  error?: boolean;
};

interface RadioItemOwnerState {
  error: boolean;
  disabled: boolean;
}

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline. */

const RadioRoot = styled(RadioGroupPrimitive.Root, {
  label: "AgenticRadio-root"
})({
  display: "grid",
  gap: ".5rem"
});

const RadioOptionRow = styled("div", {
  label: "AgenticRadio-option",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: RadioItemOwnerState }>(({ ownerState }) => [
  {
    display: "inline-flex",
    alignItems: "flex-start",
    gap: ".75rem",
    fontSize: ".875rem",
    lineHeight: "1.25rem",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.disabled
    ? {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
      }
    : { cursor: "pointer" }
]);

const RadioControl = styled(RadioGroupPrimitive.Item, {
  label: "AgenticRadio-control",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: RadioItemOwnerState }>(({ ownerState }) => [
  {
    marginTop: ".125rem",
    width: "1.25rem",
    height: "1.25rem",
    flexShrink: 0,
    cursor: "pointer",
    borderRadius: "var(--bfrs-radius-pill)",
    borderWidth: "1px",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s",
    "&:focus-visible": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .25)"),
    "&:disabled": {
      cursor: "not-allowed",
      opacity: ".5"
    },
    '&[data-state="checked"]': {
      "--tw-border-opacity": "1",
      borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))"
    }
  },
  ownerState.error
    ? {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
      }
    : {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-form-control-border) / var(--tw-border-opacity, 1))"
      }
]);

const RadioIndicator = styled(RadioGroupPrimitive.Indicator, {
  label: "AgenticRadio-indicator"
})({
  display: "flex",
  width: "100%",
  height: "100%",
  alignItems: "center",
  justifyContent: "center",
  "&:after": {
    "--tw-content": '""',
    content: "var(--tw-content)",
    width: ".625rem",
    height: ".625rem",
    borderRadius: "var(--bfrs-radius-pill)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))"
  }
});

const RadioLabel = styled("label", {
  label: "AgenticRadio-label",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: RadioItemOwnerState }>(({ ownerState }) => [
  {
    display: "grid",
    gap: ".125rem"
  },
  ownerState.disabled ? { cursor: "not-allowed" } : { cursor: "pointer" }
]);

const RadioLabelText = styled("span", {
  label: "AgenticRadio-labelText",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: RadioItemOwnerState }>(({ ownerState }) => [
  { fontWeight: 500 },
  ownerState.disabled
    ? {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
      }
    : {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
      }
]);

const RadioDescription = styled("span", {
  label: "AgenticRadio-description",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: RadioItemOwnerState }>(({ ownerState }) => [
  {
    fontSize: ".75rem",
    lineHeight: "1.25rem"
  },
  ownerState.disabled
    ? {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
      }
    : {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
      }
]);

export const Radio = forwardRef<HTMLDivElement, RadioProps>(({ className, options, error, ...props }, ref) => {
  const groupId = useId();

  return (
    <RadioRoot ref={ref} aria-invalid={error} className={className} {...props}>
      {options.map((option) => {
        const itemId = `${groupId}-${option.value}`;
        const ownerState: RadioItemOwnerState = {
          error: Boolean(error),
          disabled: Boolean(option.disabled)
        };
        return (
          <RadioOptionRow key={option.value} ownerState={ownerState}>
            <RadioControl id={itemId} value={option.value} disabled={option.disabled} ownerState={ownerState}>
              <RadioIndicator />
            </RadioControl>
            <RadioLabel htmlFor={itemId} ownerState={ownerState}>
              <RadioLabelText ownerState={ownerState}>{option.label}</RadioLabelText>
              {option.description && <RadioDescription ownerState={ownerState}>{option.description}</RadioDescription>}
            </RadioLabel>
          </RadioOptionRow>
        );
      })}
    </RadioRoot>
  );
});

Radio.displayName = "Radio";
